import SEO from '../pages/SEO';
import React from "react";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle2 } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

export default function PhysiotherapyRehabPage() {
  const programs = [
    {
      title: "Post-Surgical Rehab",
      description:
        "Structured recovery after arthroscopy, fracture fixation, or joint replacement",
    },
    {
      title: "Sports Injury Rehab",
      description:
        "Return-to-sport protocols for ligament injuries, sprains, and strains",
    },
    {
      title: "Strength Training",
      description:
        "Progressive resistance exercises to rebuild muscle strength and endurance",
    },
    {
      title: "Flexibility & Mobility",
      description:
        "Stretching and range-of-motion exercises to restore joint function",
    },
    {
      title: "Gait Training",
      description:
        "Walking and movement pattern correction for optimal biomechanics",
    },
    {
      title: "Sport-Specific Conditioning",
      description:
        "Tailored exercises to prepare you for the demands of your sport",
    },
  ];

  const benefits = [
    "Faster recovery times",
    "Reduced pain and swelling",
    "Improved strength and flexibility",
    "Better movement patterns",
    "Lower risk of re-injury",
    "Personalized treatment plans",
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Orthopedic Physiotherapy & Rehabilitation in Hyderabad"
        description="Orthopedic rehabilitation in Hyderabad for post-surgical recovery, sports injuries, strength and mobility, coordinated with Dr. Sujit Kumar’s care."
        keywords="orthopedic physiotherapy Hyderabad, sports injury rehabilitation, post surgery physiotherapy, joint rehabilitation Hyderabad"
        canonical="https://www.drsujitkumar.com/treatments/physiotherapy-rehab"
        robots="index, follow"
      />
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-primary/90 text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4 font-[family-name:var(--font-playfair)]">
            Physiotherapy & Rehabilitation
          </h1>
          <p className="text-lg text-primary-foreground/90 leading-relaxed max-w-2xl mx-auto">
            Move better, heal faster with expert rehabilitation programs.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 items-start">
            {/* Image */}
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-xl">
              <img
                src="/physiotherapy-rehabilitation-sports-medicine-recov.jpg"
                alt="Physiotherapy session"
                className="object-cover w-full h-full"
              />
            </div>

            {/* Text Content */}
            <div>
              <h2 className="text-3xl font-bold mb-6 font-[family-name:var(--font-playfair)]">
                Comprehensive Rehabilitation
              </h2>
              <div className="prose prose-lg max-w-none space-y-4">
                <p className="text-foreground leading-relaxed">
                  Our physiotherapy and rehabilitation programs are designed to help you recover from injury or surgery
                  and return to your activities with confidence. We provide post-injury and post-surgery rehab plans
                  with strength training, flexibility work, and sport-specific conditioning.
                </p>
                <p className="text-foreground leading-relaxed">
                  Each program is tailored to your specific needs, goals, and timeline. Our experienced physiotherapy
                  team works closely with you throughout your recovery, adjusting your program as you progress.
                </p>
              </div>

              {/* Benefits */}
              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">
                  Benefits of Our Programs
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-foreground text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-16 bg-muted/30">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 font-[family-name:var(--font-playfair)]">
            Our Programs
          </h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {programs.map((program, index) => (
              <Card key={index}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-2">{program.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {program.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
